// ============================================================
// Service unifié qui parle au ServerControlResource côté Quarkus.
// Chaque appel prend un scope + une key qui pointent vers UN serveur Ptero :
//   scope = 'api-dev' | 'api-beta' | 'api-preprod' | 'api-prod'
//         | 'dev' | 'beta' | 'mc-preprod' | 'mc-prod'
//   key   = apiId | devEnvId | slot A..E | identifier Ptero direct
//           (API : apiId vise la Principale, "apiId:cle" une autre instance, ex. "api-x:iris")
//
// Le backend résout (Ptero, identifier) et effectue l'action.
// ============================================================
import { apiFetch, API_BASE } from './api';
import type { ApiHealthCheck } from './api-services.service';

export type ServerScope =
  | 'api-dev' | 'api-beta' | 'api-preprod' | 'api-prod'
  | 'dev' | 'beta'
  | 'mc-preprod' | 'mc-prod';

export type PowerSignal = 'start' | 'stop' | 'restart' | 'kill';

export interface ServerState {
  /** État Ptero brut : les boutons power en dépendent. */
  state: string;       // ex: 'running' | 'offline' | 'starting' | 'stopping' | 'unknown'
  /**
   * État à afficher : celui de Ptero, ou 'error' pour un serveur d'API running qui ne répond pas
   * sur son ip:port (ou redémarre en boucle), avec `statusReason`. Absent d'un CICD plus ancien.
   */
  status?: string;
  statusReason?: string;
  /** ip:port de l'allocation (serveurs d'API seulement), sondé par le CICD en contrôle de santé "port". */
  endpoint?: string;
  /**
   * Contrôle de santé de l'API (serveurs d'API seulement) : "process" = worker sans port, seule la
   * boucle de redémarrage donne 'error'. Absent hors API et d'un CICD plus ancien.
   */
  healthCheck?: ApiHealthCheck;
  identifier: string;
  memoryBytes?: number;
  cpuAbsolute?: number;   // %
  diskBytes?: number;
  uptimeMs?: number;
  networkRxBytes?: number;
  networkTxBytes?: number;
}

export interface WsTicket {
  ticket: string;
  identifier: string;
}

const baseUrl = (pid: string, scope: ServerScope, key: string) =>
  `/api/projects/${pid}/servers/${scope}/${encodeURIComponent(key)}`;

export const serverControlService = {
  power: (pid: string, scope: ServerScope, key: string, signal: PowerSignal) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/power/${signal}`, { method: 'POST' }),

  command: (pid: string, scope: ServerScope, key: string, command: string) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/command`, {
      method: 'POST', json: { command },
    }),

  state: (pid: string, scope: ServerScope, key: string) =>
    apiFetch<ServerState>(`${baseUrl(pid, scope, key)}/state`),

  wsTicket: (pid: string, scope: ServerScope, key: string) =>
    apiFetch<WsTicket>(`${baseUrl(pid, scope, key)}/ws-ticket`, { method: 'POST' }),

  // ---- File explorer passthrough ----
  filesList: (pid: string, scope: ServerScope, key: string, directory = '/') =>
    apiFetch<{ data: Array<{ attributes: FileAttr }> }>(
      `${baseUrl(pid, scope, key)}/files/list?directory=${encodeURIComponent(directory)}`
    ),

  filesContents: async (pid: string, scope: ServerScope, key: string, file: string): Promise<string> => {
    // Endpoint retourne du text/plain — on bypass le JSON parse via fetch direct.
    const res = await fetch(
      `${API_BASE}${baseUrl(pid, scope, key)}/files/contents?file=${encodeURIComponent(file)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) throw new Error(`filesContents HTTP ${res.status}`);
    return await res.text();
  },

  filesWrite: async (pid: string, scope: ServerScope, key: string, file: string, content: string | ArrayBuffer | Blob): Promise<void> => {
    // Blob/File passé tel quel → fetch le STREAME depuis le disque (pas de chargement mémoire
    // complet façon arrayBuffer(), qui échoue sur les gros fichiers type world/db.zip).
    const body = typeof content === 'string' ? new TextEncoder().encode(content) : content;
    const res = await fetch(
      `${API_BASE}${baseUrl(pid, scope, key)}/files/write?file=${encodeURIComponent(file)}`,
      {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' },
        body,
      }
    );
    if (!res.ok) throw new Error(`filesWrite HTTP ${res.status}`);
  },

  filesCreateFolder: (pid: string, scope: ServerScope, key: string, root: string, name: string) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/files/create-folder`, {
      method: 'POST', json: { root, name },
    }),

  filesRename: (pid: string, scope: ServerScope, key: string, root: string, from: string, to: string) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/files/rename`, {
      method: 'POST', json: { root, from, to },
    }),

  filesCopy: (pid: string, scope: ServerScope, key: string, location: string) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/files/copy`, {
      method: 'POST', json: { location },
    }),

  filesDelete: (pid: string, scope: ServerScope, key: string, root: string, files: string[]) =>
    apiFetch<void>(`${baseUrl(pid, scope, key)}/files/delete`, {
      method: 'POST', json: { root, files },
    }),

  /** Télécharge un fichier tel quel. */
  filesDownload: (pid: string, scope: ServerScope, key: string, file: string) =>
    saveAs(
      `${API_BASE}${baseUrl(pid, scope, key)}/files/download?file=${encodeURIComponent(file)}`,
      baseName(file),
    ),

  /**
   * Télécharge un dossier en .zip. L'archive est construite par le backend a la volee : rien
   * n'est ecrit sur le disque du serveur de jeu, contrairement au /files/compress de Pterodactyl.
   */
  filesDownloadFolder: (pid: string, scope: ServerScope, key: string, directory: string) =>
    saveAs(
      `${API_BASE}${baseUrl(pid, scope, key)}/files/download-folder?directory=${encodeURIComponent(directory)}`,
      `${baseName(directory) || 'serveur'}.zip`,
    ),
};

function baseName(path: string): string {
  return path.replace(/\/+$/, '').split('/').pop() ?? '';
}

/**
 * Téléchargement authentifié.
 *
 * <p>Un simple lien ne convient pas : ces endpoints exigent l'en-tete Authorization, qu'une
 * navigation ne porte pas. On passe donc par un fetch puis un blob local. Consequence assumee :
 * l'archive transite par la memoire de l'onglet, d'ou les bornes posees cote backend.</p>
 *
 * <p>Le statut est verifie AVANT de fabriquer le blob : sans ca, un 403 ou un 413 serait
 * enregistre sur le disque comme un fichier du nom demande, et l'utilisateur ouvrirait un zip
 * contenant un message d'erreur JSON.</p>
 */
async function saveAs(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    let message = detail;
    try { message = (JSON.parse(detail) as { message?: string }).message ?? detail; } catch { /* pas du JSON */ }
    throw new Error(message || `Téléchargement impossible (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoquer tout de suite libererait le blob avant que le navigateur ne l'ait lu.
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  }
}

export interface FileAttr {
  name: string;
  mode: string;
  size: number;
  is_file: boolean;
  is_symlink?: boolean;
  mimetype?: string;
  created_at?: string;
  modified_at?: string;
}

/**
 * Ouvre une WS console vers le bridge Quarkus.
 * Le browser doit d'abord fetch un ws-ticket, puis appeler openConsoleSocket(ticket).
 */
export function openConsoleSocket(ticket: string): WebSocket {
  const wsBase = API_BASE.replace(/^http/i, 'ws');
  return new WebSocket(`${wsBase}/ws/console/${encodeURIComponent(ticket)}`);
}

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('recube.token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}
